import React, { useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export default function AccountSettings() {
  const { profile, coachProfile, refreshProfile } = useAuth();
  const [profileForm, setProfileForm] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    business_name: coachProfile?.business_name || ''
  });
  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Logo upload state
  const [logoUrl, setLogoUrl] = useState(coachProfile?.logo_url || null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');
  const fileInputRef = useRef(null);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'].includes(file.type)) {
      setLogoError('Please upload a JPG, PNG, WebP, or SVG file.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Logo must be under 2MB.');
      return;
    }

    setLogoError('');
    setUploadingLogo(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${profile.id}/logo.${ext}`;

      // Upload to Supabase Storage
      const { error: uploadErr } = await supabase.storage
        .from('logos')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadErr) throw uploadErr;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(path);

      // Save to coach_profiles using update (row already exists)
      const { error: saveErr } = await supabase
        .from('coach_profiles')
        .update({ logo_url: publicUrl })
        .eq('user_id', profile.id);
      if (saveErr) throw saveErr;

      setLogoUrl(publicUrl);
      await refreshProfile();
    } catch (err) {
      setLogoError(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!logoUrl) return;
    setUploadingLogo(true);
    try {
      // Remove from storage
      const ext = logoUrl.split('.').pop().split('?')[0];
      await supabase.storage.from('logos').remove([`${profile.id}/logo.${ext}`]);

      // Clear from DB
      await supabase
        .from('coach_profiles')
        .update({ logo_url: null })
        .eq('user_id', profile.id);

      setLogoUrl(null);
      await refreshProfile();
    } catch (err) {
      setLogoError(err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setSavingProfile(true);
    try {
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({
          full_name: profileForm.full_name,
          phone: profileForm.phone
        })
        .eq('id', profile.id);
      if (profileErr) throw profileErr;

      await supabase
        .from('coach_profiles')
        .upsert({
          user_id: profile.id,
          business_name: profileForm.business_name,
          hma_percentage: coachProfile?.hma_percentage || 0,
          hma_company_percentage: coachProfile?.hma_company_percentage || 0,
          mentor_percentage: coachProfile?.mentor_percentage || 0,
          status: coachProfile?.status || 'active'
        }, { onConflict: 'user_id' });

      await refreshProfile();
      setProfileSuccess('Profile updated successfully.');
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('Passwords do not match.');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    setSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: passwordForm.new_password });
      if (error) throw error;
      setPasswordSuccess('Password updated successfully.');
      setPasswordForm({ new_password: '', confirm_password: '' });
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setSavingPassword(false);
    }
  };

  const roleLabel = {
    partner: 'Partner',
    senior_coach: 'Senior Coach',
    coach: 'Coach',
    client: 'Client'
  }[profile?.role] || profile?.role;

  const showSplit = profile?.role === 'coach' || profile?.role === 'senior_coach';
  const coachPct = profile?.role === 'senior_coach' ? 80 : 75;
  const hmaPct = profile?.role === 'senior_coach' ? 20 : 25;
  const mentorPct = Number(coachProfile?.mentor_percentage || 0);
  const hmaCompanyPct = hmaPct - mentorPct;

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>Account Settings</h1>
        <p style={{ color: 'var(--text-secondary)' }}>Manage your profile and password</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 800 }}>

        {/* Profile Info */}
        <div className="card">
          <h3 style={{ fontSize: 18, marginBottom: 20, fontFamily: 'DM Sans', fontWeight: 600 }}>
            Profile Information
          </h3>

          {/* Logo Upload */}
          <div style={{ marginBottom: 24 }}>
            <div className="label" style={{ marginBottom: 8 }}>Business Logo</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Logo preview */}
              <div style={{
                width: 72, height: 72, borderRadius: 12,
                border: '2px dashed var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', background: 'var(--bg-card)', flexShrink: 0
              }}>
                {logoUrl ? (
                  <img src={logoUrl} alt="Business logo"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: 28, opacity: 0.3 }}>🏷</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                </button>
                {logoUrl && (
                  <button
                    className="btn btn-sm"
                    style={{ background: 'none', border: '1px solid var(--danger)', color: 'var(--danger)', fontSize: 12 }}
                    onClick={handleRemoveLogo}
                    disabled={uploadingLogo}
                  >
                    Remove
                  </button>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  JPG, PNG, WebP or SVG · Max 2MB
                </div>
              </div>
            </div>
            {logoError && <div className="alert alert-error" style={{ marginTop: 8 }}>{logoError}</div>}
          </div>

          {/* Role badge */}
          <div style={{ marginBottom: 20 }}>
            <div className="label">Account Type</div>
            <span style={{
              background: 'rgba(201,169,110,0.12)', color: 'var(--accent)',
              padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500
            }}>{roleLabel}</span>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Contact your partner to change your role
            </div>
          </div>

          {profileError && <div className="alert alert-error">{profileError}</div>}
          {profileSuccess && <div className="alert alert-success">{profileSuccess}</div>}

          <form onSubmit={handleSaveProfile}>
            <div className="form-group">
              <label className="label">Full Name</label>
              <input className="input" value={profileForm.full_name}
                onChange={e => setProfileForm(f => ({ ...f, full_name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="label">Email</label>
              <input className="input" value={profile?.email} disabled
                style={{ opacity: 0.5, cursor: 'not-allowed' }} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Email cannot be changed
              </div>
            </div>
            <div className="form-group">
              <label className="label">Phone</label>
              <input className="input" type="tel" value={profileForm.phone}
                onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="Optional" />
            </div>
            <div className="form-group">
              <label className="label">Business Name</label>
              <input className="input" value={profileForm.business_name}
                onChange={e => setProfileForm(f => ({ ...f, business_name: e.target.value }))}
                placeholder="Your coaching business name" />
            </div>
            <button type="submit" className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center' }} disabled={savingProfile}>
              {savingProfile ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="card">
          <h3 style={{ fontSize: 18, marginBottom: 20, fontFamily: 'DM Sans', fontWeight: 600 }}>
            Change Password
          </h3>

          {passwordError && <div className="alert alert-error">{passwordError}</div>}
          {passwordSuccess && <div className="alert alert-success">{passwordSuccess}</div>}

          <form onSubmit={handleChangePassword}>
            <div className="form-group">
              <label className="label">New Password</label>
              <input type="password" className="input"
                value={passwordForm.new_password} placeholder="Min. 8 characters"
                onChange={e => setPasswordForm(f => ({ ...f, new_password: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="label">Confirm New Password</label>
              <input type="password" className="input"
                value={passwordForm.confirm_password} placeholder="Repeat new password"
                onChange={e => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))} />
            </div>
            <button type="submit" className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center' }} disabled={savingPassword}>
              {savingPassword ? 'Updating...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>

      {/* Revenue Split Info */}
      {showSplit && (
        <div className="card" style={{ maxWidth: 800, marginTop: 24 }}>
          <h3 style={{ fontSize: 18, marginBottom: 4, fontFamily: 'DM Sans', fontWeight: 600 }}>
            My Revenue Split
          </h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
            Your revenue arrangement with HMA. Contact your partner if you have questions.
          </p>

          <div style={{ display: 'flex', height: 36, borderRadius: 8, overflow: 'hidden', marginBottom: 16, gap: 2 }}>
            <div style={{
              flex: coachPct, background: 'var(--success)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#fff'
            }}>{coachPct}%</div>
            {mentorPct > 0 && (
              <div style={{
                flex: mentorPct, background: 'var(--info)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, color: '#fff'
              }}>{mentorPct}%</div>
            )}
            <div style={{
              flex: hmaCompanyPct, background: 'var(--accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#0a0a0a'
            }}>{hmaCompanyPct}%</div>
          </div>

          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', fontSize: 14 }}>
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 2 }}>You keep</div>
              <div style={{ fontWeight: 600, color: 'var(--success)', fontSize: 20 }}>{coachPct}%</div>
            </div>
            {mentorPct > 0 && (
              <div>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 2 }}>Mentor commission</div>
                <div style={{ fontWeight: 600, color: 'var(--info)', fontSize: 20 }}>{mentorPct}%</div>
              </div>
            )}
            <div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 2 }}>HMA platform fee</div>
              <div style={{ fontWeight: 600, color: 'var(--accent)', fontSize: 20 }}>{hmaCompanyPct}%</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
